/**
 * Layout shell + section composition only (DETAIL-PAGE-V2-ARCHITECTURE §1).
 * All page data arrives as the loader DTO; interaction handlers (dialogs,
 * downloads, timeline panel) are wired by DetailPageController and default
 * to no-ops here. The report-derived sections render only once the AI report
 * has predictions; while the pipeline runs they're replaced by the live
 * progress overlay (v1:7057-7072), and when there's no report and nothing
 * processing (the applicant hasn't completed screening, or generation failed)
 * by AwaitingReportSection (DETAIL-PAGE-V2-PARITY §9 item 2) — never empty
 * `-`/"Not Available" fields. Step-data sections always render.
 *
 * Section order (fullscreen-spec §16, student-housing override 2026-08-12):
 * full screening runs header → AI Summary → tiles → Verification Summary →
 * Risk Flags → Debt → Collections → Written-Off → Bankruptcy →
 * Personal+Household → Rental → Employment → Additional Notes →
 * **Additional Documents 2nd last** → **Applicant Consent LAST**
 * (2026-08-12).
 *
 * GATING INVARIANT (converted-app vector, data-map §0): every credit/court-
 * records-derived surface renders ONLY when screeningKind === 'full' — the
 * kind discriminant gates at the render site; presence checks live inside
 * the components. The Simple toggle (full only) hides occupancy / rental /
 * employment / notes and the debt surfaces (data-map §1 hide-set);
 * Additional Documents and Applicant Consent stay.
 */
import type { ReactNode } from 'react'
import { isDemoProperty } from '#app/components/application-detail-v2/demo/demo-hooks'
import { deriveReportAction } from '#app/utils/application-detail-v2/derive'
import {
	deriveDetailReportView,
	hasAiPredictions,
	idVerificationAiStub,
} from '#app/utils/application-detail-v2/display'
import {
	deriveOrgTerms,
	deriveRequiredDocItems,
} from '#app/utils/application-detail-v2/document-checklist'
import {
	deriveRiskFlagLists,
	shouldShowRiskFlags,
} from '#app/utils/application-detail-v2/report-display'
import type { ApplicationDetailData } from '#app/utils/application-detail-v2/types'
import { resolveWorkAuthorization } from '#app/utils/application-detail-v2/work-authorization'
import { cn } from '#app/utils/misc'
import {
	ApplicationHeader,
	type HeaderActionHandlers,
} from './ApplicationHeader'
import { AwaitingReportSection } from './AwaitingReportSection'
import { ProcessingSection } from './ProcessingSection'
import { SummaryTiles, SummaryTilesView } from './SummaryTiles'
import { VerificationSummary } from './VerificationSummary'
import type { DownloadsApi } from './downloads/useDownloads'
import { DETAIL_CONTAINER } from './primitives'
import { AdditionalDocuments } from './sections/AdditionalDocuments'
import { AdditionalNotes } from './sections/AdditionalNotes'
import { AiSummary } from './sections/AiSummary'
import { ApplicantConsent } from './sections/ApplicantConsent'
import { DebtSummary } from './sections/DebtSummary'
import { Employment } from './sections/Employment'
import { Household } from './sections/Household'
import { NextOfKin } from './sections/NextOfKin'
import { PersonalDetails } from './sections/PersonalDetails'
import { RentalStatus } from './sections/RentalStatus'
import {
	BankruptcyTable,
	CollectionsTable,
	WriteOffTable,
} from './sections/ReportTables'
import { RiskFlags } from './sections/RiskFlags'

export interface DetailPageHandlers extends HeaderActionHandlers {
	onViewIdVerification?: () => void
	onViewIncomeVerification?: () => void
	onViewRentVerification?: () => void
	onViewCreditReport?: () => void
}

export function ApplicationDetailPage({
	data,
	handlers = {},
	downloadsApi,
	resumeHold,
	profileMenu,
}: {
	data: ApplicationDetailData
	handlers?: DetailPageHandlers
	downloadsApi?: DownloadsApi
	/** TT-12 held-report resume — wired by DetailPageController (it owns the
	 * fetcher posting to screening-resume-hold); absent when not held. */
	resumeHold?: { onGenerate: () => void; pending: boolean; error?: string }
	/** Account menu for the header's top bar — see ApplicationHeader. */
	profileMenu?: ReactNode
}) {
	const aiReportData = data.results?.aiReportData ?? null
	const reportSkipped = data.results?.aiProcessingStatus === 'skipped'
	const isProcessing =
		!reportSkipped &&
		(data.results?.aiProcessingStatus === 'pending' ||
			data.results?.aiProcessingStatus === 'processing')
	const hasReport = hasAiPredictions(aiReportData)
	// report | processing | awaiting | skipped (student Bank=No).
	const reportView = deriveDetailReportView({
		isProcessing,
		hasReport,
		skipped: reportSkipped,
	})
	// 'generate' only when generation FAILED and the viewer may regenerate —
	// surfaces the in-card CTA on the failed empty state (same gate as the
	// header kebab; the resource endpoint re-checks).
	const reportAction = deriveReportAction({
		canRegenerate: data.canRegenerate,
		derivedStatus: data.application.derivedStatus,
		aiProcessingStatus: data.results?.aiProcessingStatus,
	})
	// TT-12 billing hold: submitted but no report ran (wallet at/under floor at
	// submit). The Generate CTA posts to screening-resume-hold (fetcher owned
	// by DetailPageController, per-action fetcher pattern); loaders revalidate
	// after and the page flips to the processing overlay. Gated by the same
	// regenerate permission as the failed CTA (the endpoint re-checks anyway).
	const billingHold = data.application.billingHold && reportView === 'awaiting'
	// Parked guarantor (waiting marker on the results row): show the waiting
	// card, never the generating overlay or the failed CTA — the report
	// un-parks and generates automatically when the applicants are ready.
	const waitingOnApplicants =
		data.results?.reportWaitingOnApplicants === true && !hasReport
	const tenantNotes = data.steps.review?.additionalNotes
	const additionalDocuments = data.steps.review?.additionalDocuments
	// Dynamic Application Form (TT-65): the frozen config drives the documents
	// checklist and the org Terms & Conditions consent row. Legacy apps resolve
	// to the kind default (both off) → both stay null, exactly today's render.
	// Shared pure derivations (document-checklist.ts) — the combined report's
	// per-member accordions run the same ones (2026-09-08).
	const requiredDocItems = deriveRequiredDocItems(
		data.formConfig,
		data.steps.review?.requestedDocuments,
	)
	const orgTerms = deriveOrgTerms(data.formConfig)
	const isFull = data.screeningKind === 'full'
	const isGuarantor = data.application.applicationType === 'guarantor'
	const isStudentHousing = data.applicationCategory === 'student_housing'
	// The Simple|Enterprise depth toggle was REMOVED (client 2026-08-27):
	// enterprise is the only depth now, so the Simple hide-set (occupancy /
	// rental / employment / notes + the debt surfaces) is unreachable and
	// those sections render on their kind gates alone.
	const showReportSections = reportView === 'report' || reportView === 'skipped'

	return (
		<div className="min-h-screen bg-[#fbfdfd] pb-16">
			<ApplicationHeader
				data={data}
				handlers={handlers}
				downloadsApi={downloadsApi}
				profileMenu={profileMenu}
			/>

			<main className={cn(DETAIL_CONTAINER, 'mt-6 flex flex-col gap-6')}>
				{waitingOnApplicants ? (
					// Parked guarantor: deliberately waiting on the group's applicant
					// FULL reports; resumes automatically (worker events + sweep). A
					// static card — NOT ProcessingSection, whose 3-5s polling would
					// otherwise run for the whole wait.
					<AwaitingReportSection
						applicantName={data.application.applicantName}
						waitingOnApplicants
					/>
				) : null}
				{isProcessing && !waitingOnApplicants ? (
					<ProcessingSection
						applicationId={data.application.id}
						applicantName={data.application.applicantName}
						screeningKind={data.screeningKind}
						showOverlay={reportView === 'processing'}
					/>
				) : null}
				{reportView === 'awaiting' && !waitingOnApplicants && !billingHold ? (
					// No report (client 2026-09-07): the SAME summary card the report
					// render uses, titled "Application in progress" with the AI tiles
					// reading "Waiting on applicant" and the move-in date from the
					// property step — the same copy whether the applicant is still
					// working or the run FAILED (client: one wording for both; the
					// header's score slot is what distinguishes the failed case and
					// the kebab carries Generate Report). The old "Screening report
					// not generated yet" and "We hit an error…" cards are gone.
					// Billing hold keeps its own card below (the resume action lives
					// on it).
					<SummaryTilesView
						heading="Application in progress"
						pendingLabel="Waiting on applicant"
						rentRange={null}
						savings={null}
						propertyStep={data.steps.property}
						isGuarantor={data.application.applicationType === 'guarantor'}
					/>
				) : null}
				{reportView === 'awaiting' && !waitingOnApplicants && billingHold ? (
					// Billing hold keeps its card: the resume action lives on it. A
					// FAILED run no longer gets a body card (client 2026-09-07) — the
					// header's unavailable box says so and the kebab carries
					// Generate Report, mirroring the combined page.
					<AwaitingReportSection
						applicantName={data.application.applicantName}
						held={billingHold}
						onGenerate={
							billingHold
								? data.canRegenerate
									? resumeHold?.onGenerate
									: undefined
								: reportAction === 'generate'
									? handlers.onRegenerateReport
									: undefined
						}
						generatePending={resumeHold?.pending ?? false}
						generateError={resumeHold?.error}
					/>
				) : null}
				{showReportSections ? (
					<>
						<SummaryTiles
							aiReportData={reportView === 'skipped' ? null : aiReportData}
							propertyStep={data.steps.property}
							applicationType={data.application.applicationType}
							aiGeneratedPilot={data.aiSummaryPilot}
							// First row of the summary card (client 2026-08-27 — this used to be
							// its own card above). The kind gate is UNCHANGED: still full-screening
							// + a finished report, and the body self-hides without a narrative.
							aiSummary={
								isFull && reportView === 'report' ? (
									<AiSummary
										aiReportData={aiReportData}
										aiGeneratedPilot={data.aiSummaryPilot}
									/>
								) : null
							}
						/>
						<VerificationSummary
							screeningKind={data.screeningKind}
							aiReportData={
								reportView === 'skipped'
									? idVerificationAiStub({
											idStep: data.steps['id-verification'],
											hasVeriffDocuments: data.veriffDocuments.length > 0,
										})
									: aiReportData
							}
							hasVeriffDocuments={data.veriffDocuments.length > 0}
							creditAvailable={data.downloads.creditReport}
							openroom={data.openroom}
							openroomEnabled={data.openroomEnabled}
							idOnly={reportView === 'skipped'}
							onViewId={handlers.onViewIdVerification}
							onViewIncome={handlers.onViewIncomeVerification}
							onViewRent={handlers.onViewRentVerification}
							onViewCredit={
								data.can.download || isDemoProperty(data.application.propertyId)
									? handlers.onViewCreditReport
									: undefined
							}
						/>
						{/* Pre-screen flags live in the header's PreScreenFlagBanner
						    on this page, so the body card is full-screening only here
						    (client 2026-09-07); the PDF keeps the card. */}
						{reportView === 'report' &&
						shouldShowRiskFlags({
							screeningKind: data.screeningKind,
							applicationType: data.application.applicationType,
							surface: 'page',
							...deriveRiskFlagLists(aiReportData),
						}) ? (
							<RiskFlags aiReportData={aiReportData} />
						) : null}
						{reportView === 'report' && isFull ? (
							<>
								<DebtSummary aiReportData={aiReportData} />
								<BankruptcyTable aiReportData={aiReportData} />
								<CollectionsTable aiReportData={aiReportData} />
								<WriteOffTable aiReportData={aiReportData} />
							</>
						) : null}
					</>
				) : null}
				{/* Step-data sections. These used to sit behind
				    `!isFull || isEnterprise || isStudentHousing` — with the depth
				    toggle gone that gate is constantly true (isEnterprise was the
				    only false-able term), so the wrapper is dropped rather than
				    left as a no-op condition. */}
				{isGuarantor ? (
					// Guarantors have no household — show Personal Details full-width.
					// Still pass the property step: Date Of Birth lives on the adult,
					// not on the application summary.
					<PersonalDetails
						application={data.application}
						propertyStep={data.steps.property}
					/>
				) : (
					<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
						<PersonalDetails
							application={data.application}
							propertyStep={data.steps.property}
						/>
						<Household propertyStep={data.steps.property} />
						{/* Dynamic Application Form (TT-65): presence-driven — the
							    form only collects Next of Kin when the section is on. */}
						<NextOfKin propertyStep={data.steps.property} />
					</div>
				)}
				<RentalStatus
					rental={data.steps.rental}
					formConfig={data.formConfig}
					applicationType={data.application.applicationType}
					showStudentExtras={isStudentHousing && !isGuarantor}
				/>
				<Employment
					employment={data.steps.employment}
					propertyStep={data.steps.property}
					formConfig={data.formConfig}
					// Work Authorization displays under Employment for every kind
					// (QA 2026-09) — employment-step answer first, legacy
					// rental-step answers as fallback.
					workAuth={resolveWorkAuthorization({
						employment: data.steps.employment,
						rental: data.steps.rental,
					})}
					verifyIncomeStep={data.steps['verify-income']}
					showStudentExtras={isStudentHousing && !isGuarantor}
				/>
				{tenantNotes ? <AdditionalNotes notes={tenantNotes} /> : null}
				{(additionalDocuments && additionalDocuments.length > 0) ||
				(requiredDocItems && requiredDocItems.length > 0) ? (
					<AdditionalDocuments
						documents={additionalDocuments}
						requiredItems={requiredDocItems}
					/>
				) : null}
				<ApplicantConsent
					// The consent form's own timestamp (2026-09-07); a legacy row
					// without one falls back to the application's creation time —
					// consent is the first thing an applicant does, so that is the
					// closest record (client: not the Review submit time).
					consentedAt={data.consentedAt ?? data.application.applicationDate}
					orgTerms={orgTerms}
				/>
			</main>
		</div>
	)
}
